export interface CustomerPendingInvoicesResponse {
  invoices: InvoiceResponse[]
}

export interface InvoiceResponse {
  id: string
  invoice_id: string
  customer_id: string
  customer_name: string
  company_name: string
  email: string
  status: string
  number: string
  date: string
  due_date: string
  total: number
  balance: number
  source: string
  [key: string]: unknown
  tds_already_deducted: string
  is_tds_fully_deducted: boolean
  last_tds_percent: string | null
  credit_notes: CreditNoteResponse[]
  default_tds: string
  possible_tds: number[]
}

export interface CreditNoteResponse {
  creditnote_number: string
  creditnote_id: string
  creditnote_url: string
  credits_applied: number
}

export class CreditNote {
  creditNoteNumber = ""
  creditNoteId = ""
  creditNoteUrl = ""
  creditsApplied = 0

  constructor(creditNote?: CreditNoteResponse) {
    if (creditNote) {
      this.creditNoteId = creditNote?.creditnote_id ?? ""
      this.creditNoteNumber = creditNote?.creditnote_number ?? ""
      this.creditNoteUrl = creditNote?.creditnote_url ?? ""
      this.creditsApplied = creditNote?.credits_applied ?? 0
    }
  }
}

export class Invoice {
  zohoInvoiceId = ""
  balance = Infinity
  companyName = ""
  customerId = ""
  customerName = ""
  date = ""
  dueDate = ""
  email = ""
  invoiceId = ""
  invoiceNumber = ""
  status = ""
  total = 0
  source = ""
  tdsPercents: Record<string, number> = {}
  tdsAlreadyDeducted = 0
  isTdsFullyDeducted = false
  lastTdsPercent: string | null = null
  lastTdsAmount = 0
  creditNotes: CreditNote[] = []
  defaultTds = ""
  possibleTds: number[] = []

  getTdsPercent(tds: number): number {
    return this.tdsPercents[tds] ?? 0
  }

  canTdsDeducted(tdsArr: number[]) {
    if (this.isTdsFullyDeducted) {
      return false
    }

    for (const tds of tdsArr) {
      const value = this.tdsPercents[tds]
      if (typeof value === "number" && value > this.tdsAlreadyDeducted) {
        return true
      }
    }

    return false
  }

  getLastTdsAmount(invoiceDetails: InvoiceResponse): number {
    if (!invoiceDetails.last_tds_percent) {
      return 0
    }

    // Use last_tds_percent as key to get the TDS value
    const tdsValue =
      invoiceDetails[invoiceDetails.last_tds_percent as keyof InvoiceResponse]
    const tdsAmount =
      typeof tdsValue === "string" ? Number.parseFloat(tdsValue) : 0
    const alreadyDeducted = invoiceDetails.tds_already_deducted
      ? Number.parseFloat(invoiceDetails.tds_already_deducted)
      : 0

    return Math.max(0, tdsAmount - alreadyDeducted)
  }

  constructor(invoiceDetails?: InvoiceResponse) {
    if (invoiceDetails) {
      this.invoiceId = invoiceDetails?.id
      this.balance = invoiceDetails?.balance
      this.customerName = invoiceDetails?.customer_name
      this.companyName = invoiceDetails?.company_name
      this.customerId = invoiceDetails?.customer_id
      this.date = invoiceDetails?.date
      this.dueDate = invoiceDetails?.due_date
      this.email = invoiceDetails?.email
      this.zohoInvoiceId = invoiceDetails?.invoice_id
      this.invoiceNumber = invoiceDetails?.number
      this.status = invoiceDetails?.status
      this.total = invoiceDetails?.total
      this.source = invoiceDetails?.source
      this.defaultTds = invoiceDetails?.default_tds
      this.possibleTds = invoiceDetails?.possible_tds
      // Dynamically parse all tds_<rate>_percent keys
      this.tdsPercents = Object.entries(invoiceDetails)
        .filter(([key]) => /^tds_[\d.]+_percent$/.test(key))
        .reduce<Record<string, number>>((acc, [key, value]) => {
          const rateStr = key.match(/[\d.]+/)?.[0]
          const rate = rateStr !== undefined ? Number.parseFloat(rateStr) : NaN
          if (!Number.isNaN(rate) && value) {
            acc[rate.toFixed(1)] = Number.parseFloat(value as string)
          }
          return acc
        }, {})
      this.tdsAlreadyDeducted = invoiceDetails?.tds_already_deducted
        ? Number.parseFloat(invoiceDetails.tds_already_deducted)
        : 0
      this.isTdsFullyDeducted = invoiceDetails?.is_tds_fully_deducted
      this.lastTdsPercent = invoiceDetails?.last_tds_percent
        ? invoiceDetails.last_tds_percent.match(/[\d.]+/)?.[0] ?? null
        : null
      this.lastTdsAmount = this.getLastTdsAmount(invoiceDetails)
      this.creditNotes = invoiceDetails?.credit_notes
        ? invoiceDetails.credit_notes.map(
            (creditNote) => new CreditNote(creditNote),
          )
        : []
    }
  }
}
